import React, { useContext, useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { AuthContext, EventsContext, LanguageContext } from "../context"
import ShareModal from "../components/ShareModal"
import PassengerModal from "../components/PassengerModal"
import VehicleModal from "../components/VehicleModal"
import EventVehicleRow from "../components/EventVehicleRow"
import { renderDateTime, renderFullAddress } from "../utils"
import useEventActions from "../hooks/useEventActions"
import { AUTH_PROMPT_TYPE, DATE_TIME_FORMAT } from "../utils/constants"
import moment from "moment"

const EventPage = () => {
  const { id } = useParams()
  const { fetchEvent, events, eventsLoading, eventsLoaded, joinVehicle, joinWaitlist, promptEditEvent, leaveWaitlist, leaveVehicle } = useContext(EventsContext)
  const { authUser, isUserAuthenticated, promptAuth, userData } = useContext(AuthContext)
  const { t } = useContext(LanguageContext)

  const eventId = id

  const [eventData, setEventData] = useState(null)
  const [shareModal, setShareModal] = useState({
    isOpen: false,
    event: {}
  })
  const [passengerModal, setPassengerModal] = useState({
    isOpen: false,
    event: null,
    vehicle: null,
    vehicleIndex: -1,
    waitlist: false
  })
  const [vehicleModal, setVehicleModal] = useState({
    isOpen: false,
    event: null,
    vehicleIndex: -1
  })

  const [eventNotFound, setEventNotFound] = useState(false)
  const [isEventPast, setIsEventPast] = useState(false)
  const [trip1WaitlistPassengers, setTrip1WaitlistPassengers] = useState({})
  const [trip2WaitlistPassengers, setTrip2WaitlistPassengers] = useState({})

  const { isEventOrganiser, isOnWaitlist, myWaitlistPassengers, isVehiclePassenger, myVehiclePassengers } = useEventActions({ event: eventData })

  useEffect(() => {
    if (!id) return
    if (!eventsLoaded) return
    if (events.find(e => e.id === id)) {
      setEventData(events.find(e => e.id === id))
    } else if (eventNotFound) {
      setEventData(null)
    } else {
      handleFetchEvent()
    }
  }, [id, events, eventsLoaded])

  const handleFetchEvent = async () => {
    setEventNotFound(false)
    const fetchEventResponse = await fetchEvent(id)
    if (fetchEventResponse.error && fetchEventResponse.error.code === "functions/not-found") {
      setEventNotFound(true)
      return
    }
  }

  const handleShare = (event) => {
    setShareModal({ isOpen: true, event })
  }

  const handleJoinWaitlist = () => {
    let passengerData = null
    if (isOnWaitlist()) {
      let myPassengers = myWaitlistPassengers()
      passengerData = {
        firstname: myPassengers[0].user_firstname,
        lastname: myPassengers[0].user_lastname,
        phone: myPassengers[0].phone,
        email: myPassengers[0].email,
        notes: myPassengers[0].notes,
        passengers: myPassengers
      }
    }
    setPassengerModal({
      isOpen: true,
      event: eventData,
      vehicle: null,
      vehicleIndex: -1,
      waitlist: true,
      passenger: passengerData
    })
  }

  const handleAddVehicle = () => {
    setVehicleModal({
      isOpen: true,
      event: eventData,
      vehicleIndex: -1
    })
  }

  const handleEditVehicle = (vehicleIndex) => {
    setVehicleModal({
      isOpen: true,
      event: eventData,
      vehicleIndex,
      vehicle: eventData.vehicles[vehicleIndex]
    })
  }

  const handlePromptJoinVehicle = (vehicle, index) => {
    const myPassengers = myVehiclePassengers(index)
    setPassengerModal({
      isOpen: true,
      event: eventData,
      vehicle,
      vehicleIndex: index,
      waitlist: false,
      passenger: myPassengers.length === 0 ? null : {
        firstname: myPassengers[0].user_firstname,
        lastname: myPassengers[0].user_lastname,
        phone: myPassengers[0].phone,
        email: myPassengers[0].email,
        notes: myPassengers[0].notes,
        passengers: myPassengers.map(p => ({ firstname: p.firstname, trip1: p.trip1, trip2: p.trip2 }))
      }
    })
  }

  const handleCompleteJoinVehicle = async (passengerData) => {
    if (passengerModal.waitlist) {
      return await joinWaitlist(eventId, passengerData)
    }
    return await joinVehicle(eventId, passengerModal.vehicleIndex, passengerData)

  }

  const handleJoinVehicle = async (passengerData) => {
    if (!isUserAuthenticated) {
      return new Promise((resolve, reject) => {
        promptAuth(AUTH_PROMPT_TYPE.SIGNUP, { email: passengerData.email }, async () => {
          resolve(await handleCompleteJoinVehicle(passengerData))
        })
      })
    }
    return await handleCompleteJoinVehicle(passengerData)
  }

  const handleLeaveVehicle = async (passengerData) => {
    if (passengerModal.waitlist) {
      return await leaveWaitlist(eventId, passengerData)
    }
    return await leaveVehicle(eventId, passengerModal.vehicleIndex)
  }

  const updateWaitlistPassengers = () => {
    const passengers = eventData?.waitlist || []
    if (passengers.length === 0) return ''
    const myTrip1Passengers = passengers.filter(p => p.trip1).filter(p => p.user_id === authUser.uid).map(p => `${p.firstname}`)
    const otherTrip1Passengers = passengers.filter(p => p.trip1).filter(p => p.user_id !== authUser.uid).map(p => `${p.firstname}`)
    const myTrip2Passengers = passengers.filter(p => p.trip2).filter(p => p.user_id === authUser.uid).map(p => `${p.firstname}`)
    const otherTrip2Passengers = passengers.filter(p => p.trip2).filter(p => p.user_id !== authUser.uid).map(p => `${p.firstname}`)
    setTrip1WaitlistPassengers({ myPassengers: myTrip1Passengers, otherPassengers: otherTrip1Passengers })
    setTrip2WaitlistPassengers({ myPassengers: myTrip2Passengers, otherPassengers: otherTrip2Passengers })
  }

  useEffect(() => {
    updateWaitlistPassengers()
    const isPast = moment(eventData?.end, DATE_TIME_FORMAT) < moment().valueOf()
    setIsEventPast(isPast)
  }, [eventData])

  const eventTitleLabel = () => {
  }

  const isPublicView = () => {
    return !Boolean(authUser);
  }

  return (
    <div>
      {eventsLoading && <div className='global-loading'>
        <div className='global-loading__background' />
        <div className='global-loading__spinner' />
      </div>}
      {eventsLoaded && !eventData && <div className="content-parent">
        <div className="flex flex-col justify-center">
          <h4 className="event-title !text-center">{t('event.not_found')}</h4>
          <div className="mt-6 flex justify-center">
            <Link to="/"><button className="btn">{t('generic.return_to_home')}</button></Link>
          </div>
        </div>
      </div>}

      {eventsLoaded && eventData && <div className="content-parent">
        <div className="flex mb-6">
          <div className="lg:w-9/12 flex flex-wrap md:flex-nowrap flex-col md:flex-row items-center">
            <div>
              <div className="flex">
                <h4 className="event-title">
                  {eventData?.title}
                </h4>
                {isEventOrganiser() && <button className="no-bg" onClick={() => promptEditEvent(eventData)}>
                  <img src="/edit.svg" />
                </button>}
              </div>
              <p className="event-date mb-4">
                <span>{t('my_events.start')}:</span>   {renderDateTime(eventData?.start)} <br />
                <span>{t('my_events.end')}:</span> {renderDateTime(eventData?.end)} <br />
                <span>{t('my_events.address')}:</span> {renderFullAddress(eventData.address)}<br />
              </p>
            </div>

            <div className="flex mr-auto md:mr-0 md:ml-auto mt-4 md:mt-0 flex-shrink-0">
              {isEventOrganiser(eventData) && <button className="light" onClick={() => handleShare(eventData)}>
                <img src="/send.svg" />
                {t('generic.share')}
              </button>}
              {eventData?.vehicles?.length > 0 &&
                <button className="blue ml-3" disabled={isEventPast} onClick={handleAddVehicle}>
                  <img src="/directions.svg" />
                  {isEventOrganiser(eventData, authUser) ? t('vehicle.organiser_add_title') : t('event.suggest_vehicle')}
                </button>}
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-9/12">

            {/* No car */}
            {eventData?.vehicles?.length === 0 && (
              <div className="event-box px-5 py-6">
                {/* hide text with this condition: view for the author of the event when there is no car / when he has just created the event */}
                {!isEventOrganiser() && <div className="mb-6 no-car-text">{t('event.no_vehicle')}</div>}
                {isEventOrganiser(eventData) && <button className="flex mx-auto mb-6" onClick={() => handleShare(eventData)}>
                  <img src="/share.svg" />
                  {/* {t('event.share_with_group')} */}
                  {t('generic.share')}
                </button>}
                <div className="flex flex-wrap justify-center gap-4">

                  <button className="blue" disabled={isEventPast} onClick={handleAddVehicle}>
                    <img src="/group-add.svg" />
                    {isEventOrganiser(eventData, authUser) ? t('vehicle.organiser_add_title') : t('event.suggest_vehicle')}</button>
                  {!isEventOrganiser() && <button className="blue" onClick={handleJoinWaitlist}>
                    <img src="/group-add.svg" />
                    {t('passenger.waitlist_join')}</button>}
                </div>
                {isEventOrganiser() && <div className="flex flex-wrap justify-center mt-5">
                  <button className="blue text-small mx-auto wrap max-w-full" disabled={isEventPast} onClick={handleJoinWaitlist}>
                    <img src="/group-add.svg" />
                    {t('passenger.waitlist_join')}
                  </button>
                </div>}
              </div>
            )}

            {/* Has car */}
            {eventData?.vehicles?.length > 0 && (
              eventData.vehicles.map((vehicle, index) => {
                return <EventVehicleRow disabled={isEventPast} key={index} vehicle={vehicle} onEdit={() => handleEditVehicle(index)} onJoin={() => handlePromptJoinVehicle(vehicle, index)} event={eventData} index={index} />
              })
            )}
          </div>

          <div className="lg:w-3/12 lg:pl-6">
            {/* this part also need to be hidden is public view, so I added !isPublicView() condition */}
            {(!isPublicView()) && <p className="color-purple font-semibold mb-2">
              {t('event.waitlist_title')}
            </p>}
            {/* this button is duplicated, and it moved to inside the box based on: view for the author of the event when there is no car / when he has just created the event */}
            <button className="blue text-small mx-auto wrap max-w-full" disabled={isEventPast} onClick={handleJoinWaitlist}>
              <img src="/group-add.svg" />
              {t('passenger.waitlist_join')}
            </button>
            {eventData?.waitlist?.length > 0 && (
              <>
                <br />
                <br />
                <p className="">
                  {t('event.waitlist_joined_title')} :
                </p>
                {(trip1WaitlistPassengers.myPassengers?.length || trip1WaitlistPassengers.otherPassengers?.length > 0) && <>
                  <p className="color-purple font-semibold mt-3">{t('generic.trip1')}</p>
                  {trip1WaitlistPassengers.myPassengers?.length > 0 &&
                    <p className="">
                      {trip1WaitlistPassengers.myPassengers.join(", ")} <button className="no-bg inline !p-1 align-middle" onClick={handleJoinWaitlist}>
                        <img src="/edit.svg" className="w-3.5" />
                      </button>
                    </p>
                  }
                  {trip1WaitlistPassengers.otherPassengers?.length > 0 &&
                    <p className="">
                      {trip1WaitlistPassengers.otherPassengers.join(", ")}
                    </p>
                  }
                </>}
                {(trip2WaitlistPassengers.myPassengers?.length || trip2WaitlistPassengers.otherPassengers?.length > 0) && <>
                  <p className="color-purple font-semibold mt-5">{t('generic.trip2')}</p>
                  {trip2WaitlistPassengers.myPassengers?.length > 0 &&
                    <p>
                      {trip2WaitlistPassengers.myPassengers.join(", ")} <button className="no-bg inline !p-1 align-middle" onClick={handleJoinWaitlist}>
                        <img src="/edit.svg" className="w-3.5" />
                      </button>
                    </p>
                  }
                  {trip2WaitlistPassengers.otherPassengers?.length > 0 &&
                    <p>
                      {trip2WaitlistPassengers.otherPassengers.join(", ")}
                    </p>
                  }
                </>}
              </>
            )}

          </div>
        </div>
      </div>}

      <ShareModal showModal={shareModal.isOpen} onClose={() => setShareModal({ isOpen: false, event: {} })} event={shareModal.event} />
      <PassengerModal
        showModal={passengerModal.isOpen}
        onClose={() => setPassengerModal({ isOpen: false, event: null })}
        event={passengerModal.event}
        waitlist={passengerModal.waitlist}
        vehicle={passengerModal.vehicle}
        onConfirm={async data => await handleJoinVehicle(data)}
        passenger={passengerModal.passenger}
        onRemove={handleLeaveVehicle}
      />
      <VehicleModal showModal={vehicleModal.isOpen} onClose={() => setVehicleModal({ isOpen: false, event: null, vehicleIndex: -1 })} event={vehicleModal.event} vehicleIndex={vehicleModal.vehicleIndex} vehicle={vehicleModal.vehicle} />
    </div>
  )
}

export default EventPage
